import React, { useState, useEffect } from 'react';

const ITSolutionsSection = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [showScrollHint, setShowScrollHint] = useState(true);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // Only show scroll hint if it's mobile and cards overflow
      const cardsContainer = document.querySelector('.cards-container');
      if (cardsContainer) {
        const isOverflowing = cardsContainer.scrollWidth > cardsContainer.clientWidth;
        setShowScrollHint(mobile && isOverflowing);
      }
    };

    // Check mobile status on mount
    checkMobile();

    // Add event listener to update on resize
    window.addEventListener('resize', checkMobile);

    // Set timeout to hide scroll hint after 3 seconds
    const hintTimer = setTimeout(() => {
      setShowScrollHint(false);
    }, 3000);

    // Cleanup listeners
    return () => {
      window.removeEventListener('resize', checkMobile);
      clearTimeout(hintTimer);
    };
  }, []);

  const mainCardStyle = {
    backgroundImage: 'url("/bg_1.png")',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    padding: '20px',
    width: '100%',
    minHeight: 'auto',
    height: isMobile ? 'auto' : '350px',
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    position: 'relative'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '20px'
  };

  const cardsContainerStyle = {
    display: isMobile ? 'flex' : 'grid',
    gridTemplateColumns: isMobile ? 'none' : 'repeat(auto-fit, minmax(250px, 1fr))',
    overflowX: isMobile ? 'scroll' : 'visible',
    gap: '15px',
    padding: '10px 0',
    scrollSnapType: isMobile ? 'x mandatory' : 'none',
    WebkitOverflowScrolling: 'touch',
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
    position: 'relative'
  };

  const cardStyle = {
    padding: '12px',
    backgroundColor: 'white',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'transform 0.3s ease',
    minWidth: isMobile ? '250px' : 'auto',
    flex: isMobile ? '0 0 85%' : 'none',
    margin: isMobile ? '0 10px' : '0',
    scrollSnapAlign: isMobile ? 'start' : 'initial'
  };

  const iconStyle = {
    width: '40px',
    height: '40px',
    marginBottom: '15px',
    color: '#DB1666'
  };

  const titleStyle = {
    fontSize: '20px',
    fontWeight: 'bold',
    fontFamily: 'Oswald, sans-serif',
    marginBottom: '10px',
    color: '#414042'
  };

  const reasonStyle = {
    color: 'white',
    fontSize: '22px',
    fontFamily: 'Oswald, sans-serif',
    fontWeight: '400',
    lineHeight: '32px',
    marginTop: '14px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    marginBottom: '5px'
  };

  const mainTitleStyle = {
    fontSize: isMobile ? '24px' : '32px',
    fontWeight: 'bold',
    fontFamily: 'Oswald, sans-serif',
    color: 'white',
    lineHeight: isMobile ? '32px' : '40px',
    marginBottom: '15px'
  };

  const descriptionStyle = {
    fontSize: '14px',
    color: '#414042',
    lineHeight: '20px',
    margin: '0',
    fontWeight: '400',
    fontFamily: 'Inter, sans-serif',
    textAlign: 'center'
  };

  const contentWrapperStyle = {
    maxWidth: '1400px',
    margin: '0 auto',
    width: '100%',
    position: 'relative'
  };

  const scrollHintStyle = {
    position: 'absolute',
    bottom: '-40px',
    left: '50%',
    transform: 'translateX(-50%)',
    backgroundColor: 'rgba(0,0,0,0.7)',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '20px',
    fontSize: '12px',
    zIndex: 10,
    display: showScrollHint ? 'block' : 'none',
    animation: 'fadeInOut 2s ease-in-out infinite'
  };

  const styleTag = `
    @keyframes fadeInOut {
      0%, 100% { opacity: 0; }
      50% { opacity: 1; }
    }
    .cards-container::-webkit-scrollbar {
      display: none;
    }
    .cards-container {
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
  `;

  return (
    <>
      <style>{styleTag}</style>
      <div style={mainCardStyle}>
        <div style={contentWrapperStyle}>
          <div style={headerStyle}>
            <p style={reasonStyle}>REASON TO CHOOSE US</p>
            <h1 style={mainTitleStyle}>
              WE PROVIDE TRULY PROMINENT IT SOLUTIONS
            </h1>
          </div>

          <div 
            className="cards-container" 
            style={cardsContainerStyle}
          >
            <div className="card" style={cardStyle}>
              <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 14H3a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h18a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2z"/>
                <path d="M7 20h10"/>
                <path d="M9 16v4"/>
                <path d="M15 16v4"/>
              </svg>
              <h3 style={titleStyle}>INFRASTRUCTURE TECHNOLOGY</h3>
              <p style={descriptionStyle}>
                Experienced project leads with secure infrastructure solutions
              </p>
            </div>

            <div className="card" style={cardStyle}>
              <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              <h3 style={titleStyle}>PROFESSIONAL TEAM</h3>
              <p style={descriptionStyle}>
                Secure facilities and processes with professional team management
              </p>
            </div>

            <div className="card" style={cardStyle}>
              <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                <path d="M8 11l4 4 8-8"/>
              </svg>
              <h3 style={titleStyle}>SECURITY MANAGEMENT</h3>
              <p style={descriptionStyle}>
                Backup and disaster recovery facilities with advanced security protocols
              </p>
            </div>

            <div className="card" style={cardStyle}>
              <svg style={iconStyle} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="3" width="20" height="14" rx="2" ry="2"/>
                <line x1="8" y1="21" x2="16" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
              </svg>
              <h3 style={titleStyle}>DESKTOP COMPUTING</h3>
              <p style={descriptionStyle}>
                Adherence to ISO certified centers with modern computing solutions
              </p>
            </div>
          </div>
          
          {showScrollHint && (
            <div style={scrollHintStyle}>
              ← Scroll to see more →
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ITSolutionsSection;